/**
 * PaymentSuccessPage — buyer lands here after completing Stripe checkout.
 * Polls /api/payments/status/:session_id every 2s (webhook fallback) until
 * the payment flips to "paid", then celebrates.
 */
import React, { useEffect, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { CheckCircle2, Loader2, ArrowLeft } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const PaymentSuccessPage = () => {
  const [params] = useSearchParams();
  const sessionId = params.get('session_id');
  const [status, setStatus] = useState('checking');
  const [leadSession, setLeadSession] = useState(null);
  const [productId, setProductId] = useState(null);

  useEffect(() => {
    if (!sessionId) return;
    let tries = 0;
    const iv = setInterval(async () => {
      tries += 1;
      try {
        const res = await axios.get(`${API}/payments/status/${sessionId}`);
        if (res.data?.payment_status === 'paid') {
          setStatus('paid');
          if (res.data.lead_session_id) setLeadSession(res.data.lead_session_id);
          if (res.data.product_id) setProductId(res.data.product_id);
          clearInterval(iv);
        }
      } catch { /* keep polling */ }
      if (tries > 20) { setStatus('slow'); clearInterval(iv); }
    }, 2000);
    return () => clearInterval(iv);
  }, [sessionId]);

  return (
    <div className="min-h-screen bg-[#05070d] text-white flex items-center justify-center p-6">
      <div className="text-center max-w-md" data-testid="payment-success">
        {status === 'paid' ? (
          <>
            <CheckCircle2 className="w-16 h-16 text-emerald-400 mx-auto mb-4" />
            <h1 className="text-3xl font-black mb-2">Payment received. Thank you!</h1>
            <p className="text-slate-300 mb-6">
              Your receipt is on the way. Everything you unlocked is available in your dashboard within a minute.
            </p>
            {leadSession ? (
              <Link to={`/my-matches/${leadSession}`} className="inline-flex items-center gap-2 px-5 py-3 rounded-xl bg-amber-400 text-black font-bold" data-testid="success-go-dashboard">
                Go to my dashboard <ArrowLeft className="w-4 h-4 rotate-180" />
              </Link>
            ) : productId === 'dealer_founding_monthly' ? (
              <Link to="/dealer-auth" className="inline-flex items-center gap-2 px-5 py-3 rounded-xl bg-white text-black font-bold" data-testid="success-dealer-signin">
                Set up my advisor account <ArrowLeft className="w-4 h-4 rotate-180" />
              </Link>
            ) : (
              <Link to="/" className="inline-flex items-center gap-2 px-5 py-3 rounded-xl bg-amber-400 text-black font-bold">
                <ArrowLeft className="w-4 h-4" /> Back to home
              </Link>
            )}
            {productId === 'dealer_founding_monthly' && (
              <div className="mt-8 mx-auto max-w-md rounded-2xl border border-amber-400/30 bg-amber-400/5 p-5 text-left" data-testid="setup-addon-upsell">
                <p className="text-[10px] font-black uppercase tracking-[0.2em] text-amber-300 mb-1.5">Recommended add-on</p>
                <div className="flex items-baseline justify-between gap-3 mb-1">
                  <h3 className="text-base font-black text-white">Advisor Onboarding &amp; Setup</h3>
                  <span className="text-lg font-black text-white shrink-0">$499 <span className="text-[10px] font-bold uppercase text-slate-400">one-time</span></span>
                </div>
                <p className="text-sm text-slate-300 leading-relaxed mb-3">
                  White-glove launch: account configuration &amp; branding, inventory upload assistance, custom form setup, a 1-on-1 training call, launch checklist and priority first-week support.
                </p>
                <Link
                  to="/checkout/dealer_setup"
                  className="inline-flex items-center justify-center w-full gap-2 px-4 py-2.5 rounded-xl bg-amber-400 text-black font-bold text-sm hover:bg-amber-300"
                  data-testid="setup-addon-cta"
                >
                  Add Onboarding &amp; Setup
                </Link>
                <p className="mt-2 text-[11px] text-slate-400 text-center">Or skip — you can add it anytime from your dashboard.</p>
              </div>
            )}
          </>
        ) : (
          <>
            <Loader2 className="w-12 h-12 text-cyan-300 animate-spin mx-auto mb-4" />
            <h1 className="text-2xl font-black mb-2">Confirming your payment…</h1>
            <p className="text-slate-400 text-sm">
              {status === 'slow'
                ? 'Still working on it. This usually takes just a few seconds — you can safely close this tab, your receipt is on the way.'
                : 'This usually takes a few seconds.'}
            </p>
          </>
        )}
      </div>
    </div>
  );
};

export default PaymentSuccessPage;
